import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Description, Message } from '@material-ui/icons';
import { TextField, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  selectedOrganizationSelector,
  userPrivilegesSelector,
} from '../../../../redux/selector';
import {
  getKeyOrgContacts,
  getOrgUsers,
  getOrganizationList,
  getActivePublisherIds,
} from '../../../../redux/services';
import {
  Container,
  HalfContainer,
  Row,
  Title,
  EditBtn,
  Bold,
  BoldTitle,
  Center,
  TransparentContainer,
  Title as ContentTitle,
  TitleGlyph,
} from './GeneralInfo.styled';
import { PUBLISHER_GLYPH } from '../../../../Component/BeeFreeEditor/savedRows/folderGlyphs';
import EditOrgDetailsDrawer from './EditOrgDetailsDrawer';
import { limitCharacters } from '../utils';
import OrganizationContacts from './OrganizationContacts';
import TechnicalSettings from './TechnicalSettings';
import EditTechnicalSettingsDrawer from './EditTechnicalSettingsDrawer';
import {
  Text,
  Highlight,
  Wrapper,
} from './EditOrgDetailsDrawer/EditOrgDetailsDrawer.styled';
const useStyles = makeStyles(() => ({
  textField: {
    '& input:disabled': {
      color: '#212121',
    },
  },
  selectLabel: {
    background: 'white',
  },
  selectField: {
    color: '#212121',
    '&:focus': {
      backgroundColor: 'transparent',
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
}));

const GeneralInfo = () => {
  const classes = useStyles();
  const selectedOrg = useSelector(selectedOrganizationSelector);
  const userPrivileges = useSelector(userPrivilegesSelector);
  const { isSU } = userPrivileges;
  // manageUser_Admin (MANAGE_ORG_USERS_ADMIN) is held by Staff Admin and, via
  // the upward-cascading privilege seed, every higher role (Exec, Super User,
  // Super Admin, SDA). So it alone means "Staff Admin and above".
  const showRowSubscriptions =
    userPrivileges.manageUser_Admin && selectedOrg.publisher !== true;
  const [isEditDetailsDrawerVisible, setEditDetailsDrawerVisibility] =
    useState(false);
  const [
    isEditTechnicalSettingsDrawerVisibile,
    setEditTechnicalSettingsDrawerVisibility,
  ] = useState(false);
  const [shouldPageRefresh, setPageShouldRefresh] = useState(false);
  const [keyOrgContacts, setKeyOrgContacts] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  // Publisher orgs this org subscribes to: [{ id, acronym, organizationName }]
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [publisherOrgs, setPublisherOrgs] = useState([]);
  // Subscribed org IDs that are no longer active publishers (deleted all their
  // folders/rows) — hidden from the UI but preserved on save.
  const [hiddenSubscriptionIds, setHiddenSubscriptionIds] = useState([]);

  const fetchKeyContacts = async () => {
    const keyContacts = await getKeyOrgContacts(selectedOrg.id);
    setKeyOrgContacts(keyContacts);
  };

  const fetchOrgUsers = async () => {
    const orgUsers = await getOrgUsers(selectedOrg.id);
    setSelectedUsers(
      orgUsers.filter(user =>
        user.organizations.some(
          org =>
            org.orgId === selectedOrg.id &&
            org.receivesTextingHubUpdates === true
        )
      )
    );
    setOrgUsers(orgUsers);
  };

  const fetchRowSubscriptions = async () => {
    const [orgList, activeIds] = await Promise.all([
      getOrganizationList(),
      getActivePublisherIds(),
    ]);
    if (!Array.isArray(orgList)) return;

    // Dropdown candidates: any org (other than this one) that has published
    // content, regardless of its publisher flag. "Subscribable" is defined by
    // owning a Publisher Folder with rows (activeIds), not the publisher flag.
    setPublisherOrgs(
      orgList.filter(
        org => org.id !== selectedOrg.id && activeIds.includes(org.id)
      )
    );

    const saved = selectedOrg.rowSubscriptions || [];
    // Tags: only subscribed orgs that are still active publishers.
    setSelectedPublishers(
      orgList.filter(
        org => saved.includes(org.id) && activeIds.includes(org.id)
      )
    );
    // Preserve subscribed IDs that are no longer active (publisher deleted all
    // their folders/rows) — hidden from the UI, merged back in on save.
    setHiddenSubscriptionIds(saved.filter(id => !activeIds.includes(id)));
  };

  useEffect(() => {
    fetchKeyContacts();
    fetchOrgUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg.id]);

  // Separate effect so the fetch runs once privileges load. On refresh,
  // selectedOrg.id can arrive before userPrivileges, leaving showRowSubscriptions
  // false; depending on it here re-runs the fetch when it flips true.
  useEffect(() => {
    if (showRowSubscriptions) fetchRowSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg.id, showRowSubscriptions]);

  useEffect(() => {
    setPageShouldRefresh(true);
    const timer = setTimeout(() => {
      setPageShouldRefresh(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [selectedOrg]);

  if (!selectedOrg.id || shouldPageRefresh)
    return (
      <Center>
        <CircularProgress
          style={{ margin: 'auto', alignItems: 'center', color: '#007ADE' }}
        />
      </Center>
    );

  return (
    <Container>
      <HalfContainer>
        <Row>
          <Title>
            <Description size="small" />
            Details
          </Title>
          <EditBtn
            onClick={() => {
              setEditDetailsDrawerVisibility(!isEditDetailsDrawerVisible);
            }}
          >
            Edit
          </EditBtn>
        </Row>
        <TextField
          className={classes.textField}
          disabled
          label="Organization Legal Name"
          variant="outlined"
          size="small"
          value={selectedOrg.organizationName}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Acronym"
          variant="outlined"
          size="small"
          value={selectedOrg.acronym}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Public Email Address"
          variant="outlined"
          size="small"
          value={selectedOrg.email}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Public Phone Number"
          variant="outlined"
          size="small"
          value={selectedOrg.phoneNumber}
        />
        {selectedOrg.address?.map((address, index) => (
          <Row key={index}>
            <TextField
              className={classes.textField}
              disabled
              label="Street Address"
              variant="outlined"
              size="small"
              value={address.address}
              style={{ width: '45%' }}
            />
            <TextField
              className={classes.textField}
              disabled
              label="City"
              variant="outlined"
              size="small"
              value={address.city}
              style={{ width: '25%' }}
            />
            <TextField
              className={classes.textField}
              disabled
              label="State"
              variant="outlined"
              size="small"
              value={address.state}
              style={{ width: '12%' }}
            />
            <TextField
              className={classes.textField}
              disabled
              label="Zip"
              variant="outlined"
              size="small"
              value={address.zipcode}
              style={{ width: '12%' }}
            />
          </Row>
        ))}
        <TextField
          className={classes.textField}
          disabled
          label="Website"
          variant="outlined"
          size="small"
          value={selectedOrg.website}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Member Portal URL"
          variant="outlined"
          size="small"
          value={selectedOrg.memberPortalURL}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Time Zone"
          variant="outlined"
          size="small"
          value={selectedOrg.timezone?.region}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Corporation Type"
          variant="outlined"
          size="small"
          value={selectedOrg.corporationType}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Federal Employer ID Number (EIN)"
          variant="outlined"
          size="small"
          value={selectedOrg.federalEIN}
        />
        <BoldTitle>Default Sender Info</BoldTitle>
        <TextField
          className={classes.textField}
          disabled
          label="Email 'From' Name"
          variant="outlined"
          size="small"
          value={selectedOrg.senderName}
        />
        <TextField
          className={classes.textField}
          disabled
          label="Texting 'From' Name (15 character limit)"
          variant="outlined"
          size="small"
          value={
            selectedOrg.textSenderName || limitCharacters(selectedOrg.acronym)
          }
        />
        <Row>
          <TextField
            className={classes.textField}
            disabled
            label="'From' Email Prefix"
            variant="outlined"
            size="small"
            value={selectedOrg.fromEmail}
            style={{ width: '47%' }}
          />
          <Bold>@</Bold>
          <TextField
            className={classes.textField}
            disabled
            label="'From' Email Domain"
            variant="outlined"
            size="small"
            value={selectedOrg?.domains[0] || ''}
            style={{ width: '47%' }}
          />
        </Row>
        <TextField
          className={classes.textField}
          disabled
          label="'From' Texting Number"
          variant="outlined"
          size="small"
          value={selectedOrg.fromPhone}
        />
        <Title>
          <Message size="small" />
          Texting Hub Settings
        </Title>
        <BoldTitle>Daily Summary Emails</BoldTitle>
        <Text>
          Stay updated on how many contacts are waiting to hear back from your
          team each day. Daily summaries send out around 3:00 pm Monday -
          Friday.
        </Text>
        <Wrapper>
          {selectedUsers.map(({ firstName, lastName, email, _id }) => (
            <Highlight key={_id}>
              {firstName} {lastName} ({email})
            </Highlight>
          ))}
        </Wrapper>
        {showRowSubscriptions && (
          <>
            <ContentTitle>
              <TitleGlyph>{PUBLISHER_GLYPH}</TitleGlyph>
              One-Click Content Settings
            </ContentTitle>
            <BoldTitle>One-Click Content Subscriptions</BoldTitle>
            <Text>
              Subscribe to one or more One-Click Content publishers in order to
              make their content appear in the editor for you to use.
            </Text>
            <Wrapper>
              {selectedPublishers.map(({ id, acronym, organizationName }) => (
                <Highlight key={id}>
                  {acronym}, {organizationName}
                </Highlight>
              ))}
            </Wrapper>
          </>
        )}
      </HalfContainer>
      <TransparentContainer>
        <OrganizationContacts
          height={isSU ? 'calc(60vh - 50px)' : 'calc(100vh - 100px)'}
          keyOrgContacts={keyOrgContacts}
          orgId={selectedOrg.id}
        />
        {isSU && (
          <TechnicalSettings
            selectedOrg={selectedOrg}
            setEditTechnicalSettingsDrawerVisibility={
              setEditTechnicalSettingsDrawerVisibility
            }
          />
        )}
      </TransparentContainer>
      {isEditDetailsDrawerVisible && (
        <EditOrgDetailsDrawer
          orgUsers={orgUsers}
          selectedOrg={selectedOrg}
          isEditDetailsDrawerVisible={isEditDetailsDrawerVisible}
          setEditDetailsDrawerVisibility={setEditDetailsDrawerVisibility}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          showRowSubscriptions={showRowSubscriptions}
          publisherOrgs={publisherOrgs}
          selectedPublishers={selectedPublishers}
          setSelectedPublishers={setSelectedPublishers}
          hiddenSubscriptionIds={hiddenSubscriptionIds}
        />
      )}
      {isSU && isEditTechnicalSettingsDrawerVisibile && (
        <EditTechnicalSettingsDrawer
          selectedOrg={selectedOrg}
          isEditTechnicalSettingsDrawerVisibile={
            isEditTechnicalSettingsDrawerVisibile
          }
          setEditTechnicalSettingsDrawerVisibility={
            setEditTechnicalSettingsDrawerVisibility
          }
        />
      )}
    </Container>
  );
};

export default GeneralInfo;
